// Pricing engine for SociBoost
// Rules:
//  1) Never sell below supplier price
//  2) If competitor/market average is known: sale = max(market * 0.95, supplier * 1.15) — 5% under market with 15% min margin
//  3) If market is unknown: sale = supplier * 7 (fallback multiplier)
//  4) Prices are re-computed automatically at every sync and for new services
//  5) Sale prices are rounded to psychological pricing (X.99)

export const PRICING_RULES = {
  FALLBACK_MULTIPLIER: 7,       // supplier * 7 when no market data
  MARKET_UNDERCUT: 0.95,        // 5% below market average
  MIN_MARGIN_OVER_SUPPLIER: 1.15, // 15% minimum margin over supplier price
  ROUND_MODE: 'psychological'   // ends in .99 / .49
};

// Public SMM panels reference (no direct API — user must plug credentials).
export const PANEL_REFERENCES = [
  { id: 'jap', name: 'JustAnotherPanel', apiUrl: 'https://justanotherpanel.com/api/v2', requiresApiKey: true, status: 'unlinked' },
  { id: 'peakerr', name: 'Peakerr', apiUrl: 'https://peakerr.com/api/v2', requiresApiKey: true, status: 'unlinked' },
  { id: 'smmhype', name: 'SMMHype', apiUrl: 'https://smmhype.com/api/v2', requiresApiKey: true, status: 'unlinked' },
  { id: 'morethan', name: 'MoreThanPanel', apiUrl: 'https://morethanpanel.com/api/v2', requiresApiKey: true, status: 'unlinked' }
];

// Reference market averages per service ID (indicative EUR / same quantity as base variant).
// Only a curated subset is filled; fallback ×7 applies elsewhere.
// Source: public price surveys (2026) — user should override via admin panel or API.
export const MARKET_DATA = {
  // TikTok
  't1': { unit: 'per_1000_qty', tiers: { 1000: 12.99, 5000: 44.99, 10000: 79.99, 25000: 179.99 } },     // TikTok Followers
  't2': { unit: 'per_1000_qty', tiers: { 1000: 4.90,  5000: 19.90,  25000: 79.90 } },                    // TikTok Likes
  't3': { unit: 'per_1000_qty', tiers: { 10000: 3.90, 50000: 14.90 } },                                  // TikTok Views
  // Instagram
  'i1': { unit: 'per_1000_qty', tiers: { 1000: 14.90, 5000: 54.90, 10000: 99.90 } },                     // IG Followers
  'i2': { unit: 'per_1000_qty', tiers: { 500: 3.90,  2500: 14.90,  10000: 49.90 } },                     // IG Likes
  'i3': { unit: 'per_1000_qty', tiers: { 5000: 4.90, 25000: 19.90 } },                                   // IG Reels views
  // YouTube
  'y1': { unit: 'per_1000_qty', tiers: { 500: 19.90, 2500: 89.90, 10000: 299.90 } },                     // YT Subs
  'y2': { unit: 'per_1000_qty', tiers: { 5000: 9.90, 25000: 39.90 } },                                   // YT Views
  'y3': { unit: 'per_1000_qty', tiers: { 500: 6.90,  2500: 24.90 } },                                    // YT Likes
  // Google Reviews
  'g1': { unit: 'per_1',        tiers: { 5: 29.90, 10: 54.90, 25: 129.90 } },                            // Avis Google 5*
  'g2': { unit: 'per_1',        tiers: { 5: 44.90, 10: 84.90 } },                                        // Avis perso
  'g3': { unit: 'per_1',        tiers: { 10: 59.90 } },                                                  // Google Maps note boost
  // Trustpilot
  'tp1': { unit: 'per_1',       tiers: { 5: 34.90, 10: 64.90, 25: 149.90 } },
  // Twitter (X)
  'x1': { unit: 'per_1000_qty', tiers: { 500: 12.90, 2500: 54.90, 10000: 199.90 } },                     // X Followers
  'x2': { unit: 'per_1000_qty', tiers: { 500: 4.90,  2500: 19.90 } },                                    // X Likes
  // Spotify
  'sp1': { unit: 'per_1000_qty', tiers: { 5000: 9.90, 25000: 39.90 } },                                  // Plays
  'sp2': { unit: 'per_1000_qty', tiers: { 500: 12.90, 2500: 49.90 } },                                   // Followers
  // Telegram
  'tg1': { unit: 'per_1000_qty', tiers: { 500: 9.90, 2500: 39.90 } },                                    // Members
  // Twitch
  'tw1': { unit: 'per_1000_qty', tiers: { 500: 12.90, 2500: 54.90 } },
  // Kick
  'k1': { unit: 'per_1000_qty', tiers: { 500: 11.90, 2500: 49.90 } },
  // Discord
  'd1': { unit: 'per_1',        tiers: { 100: 7.90, 1000: 49.90 } },
  // Snapchat
  's1': { unit: 'per_1',        tiers: { 500: 9.90, 2500: 39.90 } },
  // Facebook
  'f1': { unit: 'per_1000_qty', tiers: { 1000: 15.90, 5000: 59.90 } }
};

// Choose the closest available market price for a given qty.
// Returns null if qty is outside the defined market tier range (prevents extrapolation errors).
const pickMarketForQty = (marketEntry, qty) => {
  if (!marketEntry?.tiers) return null;
  const keys = Object.keys(marketEntry.tiers).map(Number).sort((a, b) => a - b);
  if (keys.length === 0) return null;
  const min = keys[0];
  const max = keys[keys.length - 1];
  // Only trust market data within its defined range (no extrapolation).
  if (qty < min * 0.5 || qty > max * 2) return null;
  // Exact match
  if (marketEntry.tiers[qty] != null) return marketEntry.tiers[qty];
  // Linear interpolation between the two closest tiers when qty is inside range.
  const below = [...keys].filter(k => k <= qty).pop();
  const above = keys.find(k => k >= qty);
  if (below != null && above != null && below !== above) {
    const t = (qty - below) / (above - below);
    return marketEntry.tiers[below] + (marketEntry.tiers[above] - marketEntry.tiers[below]) * t;
  }
  if (below != null) {
    // Same-tier proportional scaling for slight extrapolation within 2×.
    return marketEntry.tiers[below] * (qty / below);
  }
  if (above != null) {
    return marketEntry.tiers[above] * (qty / above);
  }
  return null;
};

const roundPsychological = (n) => {
  if (n <= 0) return 0.99;
  if (n < 2) return 0.99;
  if (n < 5) {
    // round to nearest .49 or .99
    const base = Math.floor(n);
    const frac = n - base;
    return base + (frac < 0.75 ? 0.49 : 0.99);
  }
  // larger amounts: floor + .99
  return Math.floor(n) + 0.99;
};

/**
 * Compute a final sale price from supplier + optional market average.
 * @param {number} supplier - what we pay to our supplier for that qty (EUR)
 * @param {number|null|undefined} market - competitor market average for the same qty (EUR)
 */
export const computeSalePrice = (supplier, market) => {
  if (typeof supplier !== 'number' || supplier <= 0) return 0.99;
  let sale;
  if (typeof market === 'number' && market > 0) {
    const target = market * PRICING_RULES.MARKET_UNDERCUT;
    const floor = supplier * PRICING_RULES.MIN_MARGIN_OVER_SUPPLIER;
    sale = Math.max(target, floor);
  } else {
    sale = supplier * PRICING_RULES.FALLBACK_MULTIPLIER;
  }
  // Enforce absolute hard-floor: never below supplier
  sale = Math.max(sale, supplier * 1.0001);
  return Number(roundPsychological(sale).toFixed(2));
};

/**
 * Deep-clone products and inject computed sale prices per variant.
 * Preserves categories, names, ids, descriptions untouched.
 */
export const applyPricing = (products) => {
  const out = {};
  for (const [platformKey, list] of Object.entries(products)) {
    out[platformKey] = list.map(prod => {
      const marketEntry = MARKET_DATA[prod.id] || null;
      const variants = prod.variants.map(v => {
        const market = pickMarketForQty(marketEntry, v.qty);
        const supplier = v.supplier != null ? v.supplier : v.price / PRICING_RULES.FALLBACK_MULTIPLIER;
        const sale = computeSalePrice(supplier, market);
        return { ...v, supplier: Number(supplier.toFixed(4)), market: market != null ? Number(market.toFixed(2)) : null, price: sale };
      });
      return { ...prod, variants };
    });
  }
  return out;
};

/**
 * Compute margin (€ and %) for a variant.
 */
export const computeMargin = (sale, supplier) => {
  if (!supplier || supplier <= 0) return { eur: sale, pct: 100 };
  return {
    eur: Number((sale - supplier).toFixed(2)),
    pct: Number((((sale - supplier) / supplier) * 100).toFixed(1))
  };
};
